import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Spinner,
  Card,
  CardBody,
} from "reactstrap";
import { FaPlus, FaTrash, FaSearch, FaImage } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { productActions } from "../../sagas/productSlice";
import { formatPrice } from "../../helpers/utility";
import productService from "../../services/productService";
import Swal from "sweetalert2";
import config from "../../helpers/config";
import "./CommandeLine.css";
import POSKeypadModal from "./POSKeypadModal";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import POSProductFormModal from "./POSProductFormModal";

const ReceptionLines = ({ formik, readOnly, mode = "full" }) => {
  const { values, setFieldValue } = formik;
  const dispatch = useDispatch();

  const [items, setItems] = useState(values.lignes || []);
  const [chooseProductModal, setChooseProductModal] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keypadValue, setKeypadValue] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [qtyKeypadOpen, setQtyKeypadOpen] = useState(false);
  const [qtyValue, setQtyValue] = useState("");
  const [qtyIndex, setQtyIndex] = useState(null);
  const [productModal, setProductModal] = useState(false);

  const unites = useSelector((state) => state.products?.unites || []);
  const { list: categories } = useSelector((state) => state.categories || {});
  const { products, loading } = useSelector((state) => state.products);

  useEffect(() => {
    setItems(values.lignes || []);
  }, [values.lignes]);

  useEffect(() => {
    dispatch(
      productActions.findAll({
        search: searchValue,
        page: 0,
        size: 200,
      })
    );
  }, [dispatch]);

  const recalcTotals = (newItems) => {
    let ht = 0;
    let ttc = 0;
    let taxe = 0;

    newItems.forEach((item) => {
      const itemTVA = item.montantTTC - item.montantHT;
      ht += item.montantHT;
      ttc += item.montantTTC;
      taxe += itemTVA;
    });

    setFieldValue("lignes", newItems);
    setFieldValue("montantTotalHT", Number(ht.toFixed(2)));
    setFieldValue("montantTotalTaxe", Number(taxe.toFixed(2)));
    setFieldValue("montantTotalTTC", Number(ttc.toFixed(2)));
  };

  const openPriceKeypad = (index, currentValue) => {
    setEditIndex(index);
    setKeypadValue(currentValue?.toString() || "");
    setKeypadOpen(true);
  };

  const openQtyKeypad = (index, currentQty) => {
    setQtyIndex(index);
    setQtyValue(currentQty?.toString() || "1");
    setQtyKeypadOpen(true);
  };

  const confirmQtyChange = (value) => {
    const newQty = parseFloat(value);
    if (!newQty || newQty <= 0) return;

    const newItems = [...items];
    const item = newItems[qtyIndex];

    const prixTTC = item.prixUnitaireTTC;
    const tva = item.tauxTVA;

    item.quantiteRecue = newQty;
    item.montantTTC = Number((prixTTC * newQty).toFixed(2));
    item.montantHT = Number((item.montantTTC / (1 + tva)).toFixed(2));

    setItems(newItems);
    recalcTotals(newItems);
    setQtyKeypadOpen(false);
  };

  const confirmPriceChange = (value) => {
    const newItems = [...items];
    const item = newItems[editIndex];

    const tva = item.tauxTVA;
    const qty = item.quantiteRecue;
    const prixTTC = parseFloat(value) || 0;
    const prixHT = prixTTC / (1 + tva);

    item.prixUnitaireTTC = Number(prixTTC.toFixed(2));
    item.prixUnitaireHT = Number(prixHT.toFixed(2));
    item.montantHT = Number((prixHT * qty).toFixed(2));
    item.montantTTC = Number((prixTTC * qty).toFixed(2));

    setItems(newItems);
    recalcTotals(newItems);
    setKeypadOpen(false);
  };

  const updateQty = (index, newQty) => {
    if (newQty <= 0) return;

    const newItems = [...items];
    const item = newItems[index];

    const prixTTC = item.prixUnitaireTTC;
    const tva = item.tauxTVA;

    item.quantiteRecue = newQty;
    item.montantTTC = Number((prixTTC * newQty).toFixed(2));
    item.montantHT = Number((item.montantTTC / (1 + tva)).toFixed(2));

    setItems(newItems);
    recalcTotals(newItems);
  };

  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    recalcTotals(newItems);
  };

  const handleProductSelect = async (product) => {
    if (!values.fournisseurId) {
      Swal.fire("Attention", "Choisissez d'abord un fournisseur", "warning");
      return;
    }

    const exists = items.some((i) => i.articleId === product.id);
    if (exists) {
      Swal.fire("Deja ajoute", "Produit deja dans la reception", "info");
      return;
    }

    let supplierPrice = await productService.findPrixAchat(product.id, values.fournisseurId);
    supplierPrice = parseFloat(supplierPrice) || 0;

    const tva = product.tauxTVAAchat;
    const prixHT = supplierPrice;
    const prixTTC = prixHT * (1 + tva);

    const newItem = {
      articleId: product.id,
      articleNom: product.libelle,
      unite: product.unite,
      quantiteRecue: 1,
      prixUnitaireHT: Number(prixHT.toFixed(2)),
      prixUnitaireTTC: Number(prixTTC.toFixed(2)),
      tauxTVA: product.tauxTVAAchat,
      montantHT: Number(prixHT.toFixed(2)),
      montantTTC: Number(prixTTC.toFixed(2)),
    };

    const newItems = [...items, newItem];
    setItems(newItems);
    recalcTotals(newItems);
    setChooseProductModal(false);
  };

  const handleSearchConfirm = () => {
    setSearchKeypadOpen(false);
    dispatch(
      productActions.findAll({
        search: searchValue,
        page: 0,
        size: 200,
      })
    );
  };

  const renderCart = () => (
    <div className="pos-cart">
      {items.length === 0 ? (
        <div className="pos-empty">Aucun article ajoute</div>
      ) : (
        items.map((item, index) => (
          <div key={index} className="pos-cart-row">
            <div className="pos-cart-info">
              <div className="pos-cart-name">{item.articleNom}</div>
              <div className="pos-cart-sub">
                {item.unite}
              </div>
            </div>

            <div className="pos-cart-controls">
              <div
                className="pos-qty-input editable"
                onClick={() => !readOnly && openQtyKeypad(index, item.quantiteRecue)}
              >
                <span className="pos-qty-input-label">Qte</span>
                <span className="pos-qty-input-value">{item.quantiteRecue}</span>
              </div>
            </div>

            <div
              className="pos-cart-price editable"
              onClick={() => !readOnly && openPriceKeypad(index, item.prixUnitaireTTC)}
            >
              {formatPrice(item.prixUnitaireTTC)}
              <div className="pos-price-sub">TTC</div>
            </div>

            <div className="pos-cart-price">{formatPrice(item.montantTTC)}</div>

            <button
              className="pos-delete-btn"
              onClick={() => !readOnly && handleDeleteItem(index)}
              type="button"
            >
              <FaTrash />
            </button>
          </div>
        ))
      )}
    </div>
  );

  const renderCatalog = () => (
    <>
      <div className="mb-3 position-relative d-flex gap-2">
        <div className="pos-product-search flex-grow-1" onClick={() => setSearchKeypadOpen(true)}>
          <FaSearch />
          <div className="pos-search-display">{searchValue || "Rechercher produit..."}</div>
        </div>

        {!readOnly && (
          <Button
            color="success"
            className="fw-bold px-3 rounded-3"
            onClick={() => setProductModal(true)}
            type="button"
          >
            <FaPlus className="me-1" /> Nouveau
          </Button>
        )}
      </div>

      <div className="pos-products-container reception-products-container">
        <Row className="g-3">
          {loading ? (
            <Col xs={12} className="text-center py-5">
              <Spinner />
            </Col>
          ) : products?.length ? (
            products.map((product) => (
              <Col xl={3} lg={4} md={6} sm={6} xs={12} key={product.id}>
                <Card
                  className="pos-product-card h-100"
                  onClick={() => !readOnly && handleProductSelect(product)}
                >
                  <div className="pos-product-image">
                    {product.photo ? (
                      <img src={`${config.baseURL}${product.photo}`} alt={product.libelle} />
                    ) : (
                      <FaImage size={34} className="text-muted" />
                    )}
                  </div>

                  <CardBody className="pos-product-body">
                    <div className="pos-product-title">{product.libelle}</div>
                    <div className="pos-product-meta">
                      <span>{product.unite || "-"}</span>
                    </div>
                    <div className="pos-product-footer">
                      <div className="price">{formatPrice(product.prixVenteTTC1 || 0)}</div>
                      {!readOnly && (
                        <button type="button" className="add-btn">
                          <FaPlus />
                        </button>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <div className="pos-empty">Aucun produit trouve</div>
            </Col>
          )}
        </Row>
      </div>
    </>
  );

  return (
    <>
      {mode === "catalog" && renderCatalog()}
      {mode === "cart" && renderCart()}
      {mode === "full" && (
        <>
          {renderCart()}

          <div
            className="pos-add-bar"
            type="button"
            onClick={() => !readOnly && setChooseProductModal(true)}
          >
            <FaPlus /> Ajouter produit
          </div>

          <Modal isOpen={chooseProductModal} toggle={() => setChooseProductModal(false)} size="xl">
            <ModalHeader
              toggle={() => setChooseProductModal(false)}
              className="d-flex justify-content-between align-items-center"
            >
              <span>Choisir Produit</span>

              {!readOnly && (
                <Button
                  color="success"
                  size="sm"
                  className="fw-bold px-3"
                  onClick={() => setProductModal(true)}
                >
                  <FaPlus className="me-1" /> Nouveau produit
                </Button>
              )}
            </ModalHeader>

            <ModalBody>{renderCatalog()}</ModalBody>
            <ModalFooter>
              <Button onClick={() => setChooseProductModal(false)}>Fermer</Button>
            </ModalFooter>
          </Modal>
        </>
      )}

      <POSNumericKeypadModal
        isOpen={keypadOpen}
        toggle={() => setKeypadOpen(false)}
        value={keypadValue}
        onChange={setKeypadValue}
        onConfirm={confirmPriceChange}
        title="Modifier Prix TTC"
      />

      <POSNumericKeypadModal
        isOpen={qtyKeypadOpen}
        toggle={() => setQtyKeypadOpen(false)}
        value={qtyValue}
        onChange={setQtyValue}
        onConfirm={confirmQtyChange}
        title="Saisir Quantite"
      />

      <POSKeypadModal
        isOpen={searchKeypadOpen}
        toggle={() => setSearchKeypadOpen(false)}
        value={searchValue}
        onChange={setSearchValue}
        onSearch={handleSearchConfirm}
        title="Recherche Produit"
      />

      <Modal
        isOpen={productModal}
        toggle={() => setProductModal(false)}
        size="xl"
        centered
        className="pos-product-modal"
      >
        <ModalHeader toggle={() => setProductModal(false)}>{"Nouveau produit"}</ModalHeader>

        <ModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <POSProductFormModal
            onClose={() => {
              setProductModal(false);
              handleSearchConfirm();
            }}
            categories={categories}
            unites={unites}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default ReceptionLines;
